import { useMemo } from 'react';
import type { ReactNode, Ref } from 'react';
import { Email } from '@kula-ai/components';
import type {
  EmailApiHandle,
  EmailCompositionValue,
  EmailProps,
  SelectOption,
} from '@kula-ai/components';
import { useSelector } from 'react-redux';
import { uploadUserInlineImage } from '@/api/userImageUploads/useUploadUserInlineImage';
import { customTemplateSelectProps } from '@/components/shared/customTemplateLabel';
import {
  useCcBccUserOptions,
  useSenderAutocomplete,
  asyncSelectProps,
  mergeOptionsByValue,
} from '@/api/userSearch/useCcBccUserOptions';
import { isTemplateEdited } from './templateSelection';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { selectUsers } from '@/store/users/selectors';
import { useEmailComposer } from './useEmailComposer';
import { toSelectOptions } from './emailValue';
import type { EmailUser, EmailValueKey } from './emailValue';

interface EmailComposerProps<TPatch extends Record<string, unknown>> {
  formik?: any;
  toValue: (_values: any) => EmailCompositionValue;
  toPatch: (
    _value: EmailCompositionValue,
    _resolveUser: (_value: string) => EmailUser | undefined
  ) => TPatch;
  /** Defaults to the delegatable senders; pass to restrict who may send. */
  fromUsers?: EmailUser[];
  /** Defaults to every user. */
  ccBccUsers?: EmailUser[];
  /** Recipients already saved on the record, so their labels survive (ENG-3766). */
  savedUsers?: EmailUser[];
  valueKey?: EmailValueKey;
  // Hook seams — see `useEmailComposer`.
  applyPatch?: (_patch: Record<string, unknown>) => void;
  seedKey?: unknown;
  normalise?: (
    _prev: EmailCompositionValue,
    _next: EmailCompositionValue
  ) => EmailCompositionValue;
  onTemplateChange?: (
    _templateId: string | null,
    _previousId: string | null,
    _patch: TPatch
  ) => Record<string, unknown> | void;
  /** Sender adopted once the list confirms it is selectable. */
  wantedFrom?: string;
  /** Template whose subject and body are applied when its id changes. */
  selectedTemplate?: {
    id?: string | number;
    subject?: string;
    body?: string;
  } | null;

  /** Supplying options moves the template picker into the card. */
  templateOptions?: SelectOption[];
  templateSelectProps?: Record<string, unknown>;
  /**
   * Merged onto the From `Select` last. For chrome the surface owns — the menu
   * portal and the sender list stay this component's business.
   */
  fromSelectProps?: Record<string, unknown>;
  /** Added to the editor box, e.g. to change how it joins the rows above it. */
  editorClassName?: string;
  /**
   * Extra props forwarded to the editor frame — height, min/max height, content
   * classes. Spread first, so the composer's own editor wiring still wins.
   */
  editorProps?: Omit<
    EmailProps['editorProps'],
    'placeholder' | 'toolbarFeatures' | 'variable' | 'extraExtensions'
  >;
  /** Recipients the surface picks itself; omit for a candidate-fixed email. */
  toOptions?: SelectOption[];
  toMode?: 'single' | 'multiple';
  /** Merged over the editor defaults. */
  toolbarFeatures?: Record<string, boolean>;
  variables?: Record<string, string[]>;
  subjectMaxLength?: number;
  /** Overrides `submitCount`, for surfaces that gate their own error display. */
  submitAttempt?: number;
  /** Surfaces that mirror kula's inline errors into their own footer. */
  onInlineErrorChange?: (_errors: unknown) => void;
  /** Attachment chips and handlers — see `useEmailAttachments`. */
  attachments?: Record<string, unknown>;
  extraExtensions?: unknown[];
  toolbarStartSlot?: ReactNode;
  /**
   * The trailing toolbar cluster. kula renders it immediately before the variable
   * picker, where `toolbarStartSlot` lands among the formatting controls.
   */
  toolbarEndSlot?: ReactNode;
  /**
   * kula's handle on the organism. `focusFirstError()` scrolls the highest
   * priority error into view and focuses it — from → to → subject → content —
   * so a surface that blocks its own submit can put the reason on screen
   * rather than leaving the reader to find it.
   */
  apiRef?: Ref<EmailApiHandle>;
  /**
   * Merged onto the To / CC / BCC selects. Surfaces that replace the recipient
   * menu pass `reactSelectProps` here — kula spreads it last, so it wins over
   * the async-search props this composer sets.
   */
  recipientSelectProps?: Record<string, unknown>;
  /**
   * The same props, per field, called with the composer's live selection.
   *
   * Cc and Bcc are two separate Selects sharing one recipient budget and neither
   * can see the other's value — this component is the only place that holds
   * both. Absent, every field keeps the single shared `recipientSelectProps`,
   * which is what every consumer but the internal-email composer wants.
   */
  recipientSelectPropsFor?: (
    _field: 'to' | 'cc' | 'bcc',
    _selected: { to: string[]; cc: string[]; bcc: string[] }
  ) => Record<string, unknown>;
  /**
   * Where the From menu portals. `recipientSelectProps` carries this for To /
   * CC / BCC, but it also carries the recipient menu itself, so From cannot
   * share it — From has to be told separately or it keeps kula's default of
   * `document.body`, which a kula Drawer makes unclickable.
   */
  menuPortalTarget?: HTMLElement | null;
  /** Rendered inside the email card below the editor, sharing its border chrome. */
  footerAddon?: ReactNode;
  onEditorReady?: (_editor: unknown) => void;
  editorPlaceholder?: string;
  disabled?: boolean;
}

// Every surface renders these identically; they are the composer's shape, not
// a per-surface choice.
const PLACEHOLDERS = {
  from: 'Select from',
  to: 'Select recipients',
  subject: 'Enter subject',
  template: 'Select email template',
} as const;

/**
 * The shared kula `Email` composer.
 *
 * Owns the state machine (via `useEmailComposer`) and the props every surface
 * rendered the same way, so a surface supplies only its mappers, its toolbar
 * and whatever it genuinely does differently.
 */
export const EmailComposer = <TPatch extends Record<string, unknown>>({
  formik,
  toValue,
  toPatch,
  fromUsers,
  ccBccUsers,
  savedUsers,
  valueKey,
  applyPatch,
  seedKey,
  normalise,
  onTemplateChange,
  wantedFrom,
  selectedTemplate,
  templateOptions,
  templateSelectProps,
  fromSelectProps: fromSelectPropsOverride,
  editorClassName,
  editorProps,
  toOptions,
  toMode = 'multiple',
  toolbarFeatures,
  variables,
  subjectMaxLength,
  submitAttempt,
  onInlineErrorChange,
  attachments,
  extraExtensions,
  toolbarStartSlot,
  toolbarEndSlot,
  apiRef,
  footerAddon,
  recipientSelectProps,
  recipientSelectPropsFor,
  menuPortalTarget,
  onEditorReady,
  editorPlaceholder = 'Start typing...',
  disabled = false,
}: EmailComposerProps<TPatch>) => {
  // Every surface wired these two identically, so they are the default here.
  const { fromUsers: delegatableSenders, isFromUsersLoading } =
    useGetAllUsers();
  const allUsers = useSelector(selectUsers);

  // CC/BCC is searched server-side instead of filtered over the redux copy of
  // the account, which is a page of users and never the whole list (ENG-3766).
  //
  // Those users still go in: in async mode the menu comes from `loadOptions`,
  // while the selected chips resolve their labels from this list. Surfaces
  // that store cc/bcc as bare ids have nothing else to resolve them with, so
  // dropping it rendered saved recipients as raw ids.
  const knownCcBccOptions = useMemo(
    () =>
      toSelectOptions(
        [
          ...(ccBccUsers ?? (allUsers as EmailUser[]) ?? []),
          ...(savedUsers ?? []),
        ],
        valueKey
      ),
    [ccBccUsers, allUsers, savedUsers, valueKey]
  );
  const { initialOptions: searchedCcBccOptions, loadOptions: loadCcBcc } =
    useCcBccUserOptions(knownCcBccOptions, valueKey);
  const ccBccSelectProps = useMemo(
    () => ({ ...asyncSelectProps(loadCcBcc), ...(recipientSelectProps ?? {}) }),
    [loadCcBcc, recipientSelectProps]
  );

  // From searches the account too, the way the interview emails do. The
  // delegatable list is what the field opens on; `searchedSenders` feeds back
  // into the composer so a searched pick still resolves to a user object —
  // without it a stale `fromUserObject` would outlive the selection.
  const senders = useMemo(
    () => fromUsers ?? (delegatableSenders as EmailUser[]) ?? [],
    [fromUsers, delegatableSenders]
  );
  /**
   * What the From menu may offer: the senders, and nobody else.
   *
   * `savedUsers` is a label list — the records already on the form, so a value
   * outside the fetched page still renders a name (ENG-3766) — and merging it
   * in here quietly promoted it to a permission list. On the interview plan it
   * also carries the activity's cc and bcc participants, so the menu offered
   * recipients as senders; and a sender saved before their mailbox was
   * disconnected stayed pickable afterwards. Either one is refused with
   * "Sender's mailbox is not connected" — a 422 raised only once the whole
   * email has been written (ENG-3980).
   *
   * `senders` is the authority. `/users?scope=email_delegatable` returns the
   * account in one unpaginated list, already narrowed server-side to users
   * with a connected mailbox, plus the shared mailboxes this user may send
   * from. The saved records still reach `fromOptions` below, which is what
   * labels the selected value — so dropping them here takes them out of the
   * menu without blanking the row on a saved activity.
   *
   * Still deduped by value: shared mailboxes and delegatable users are
   * concatenated upstream, and the same person must not appear twice
   * (ENG-3945).
   */
  const senderBaseOptions = useMemo(
    () => mergeOptionsByValue(toSelectOptions(senders, valueKey)),
    [senders, valueKey]
  );
  const { fromSelectProps: senderSelectProps, searchedSenders } =
    useSenderAutocomplete(senderBaseOptions, valueKey);

  // Says "loading" while the delegatable senders are in flight, rather than
  // "no users found" — the menu opens on them, and they arrive from a query.
  const fromSelectProps = useMemo(
    () => ({
      ...senderSelectProps,
      isLoading: isFromUsersLoading,
      ...(menuPortalTarget === undefined
        ? {}
        : {
            reactSelectProps: {
              ...(senderSelectProps as { reactSelectProps?: object })
                ?.reactSelectProps,
              menuPortalTarget,
            },
          }),
      ...(fromSelectPropsOverride ?? {}),
    }),
    [
      senderSelectProps,
      isFromUsersLoading,
      menuPortalTarget,
      fromSelectPropsOverride,
    ]
  );
  const knownSenders = useMemo(
    () => [...senders, ...(searchedSenders as EmailUser[])],
    [senders, searchedSenders]
  );

  const {
    value,
    handleChange,
    fromOptions,
    submitAttempt: submitCount,
  } = useEmailComposer({
    formik,
    toValue,
    toPatch,
    fromUsers: knownSenders,
    ccBccUsers: ccBccUsers ?? (allUsers as EmailUser[]),
    savedUsers,
    valueKey,
    applyPatch,
    seedKey,
    normalise,
    onTemplateChange,
    wantedFrom,
    selectedTemplate,
  });

  // `submitAttempt` only re-fires scroll/focus; `validateOnMount` is what makes
  // the organism surface its own inline errors instead of waiting for a blur.
  // Without it a never-focused Subject showed nothing and the surface had to
  // fall back to a toast.
  const attempted = submitAttempt ?? submitCount;

  const editorVariables = useMemo(() => ({ vars: variables }), [variables]);

  const subjectInputProps = useMemo(
    () => (subjectMaxLength ? { maxLength: subjectMaxLength } : undefined),
    [subjectMaxLength]
  );

  // Derive the flag apart from the props object: `value` changes on every
  // keystroke, so rebuilding the object here would hand kula's template
  // `Select` a new identity each time.
  const templateEdited = useMemo(() => {
    const selected = templateOptions?.find(
      (option) => option.value === value.template
    );
    return isTemplateEdited(value, selected);
  }, [templateOptions, value]);

  /**
   * What each recipient field currently holds, for the budget the Cc and Bcc
   * fields share. `to` is `string | string[]` in kula's value, hence the flatten
   * — the same guard `SendInternalEmailComposer` already applies to it.
   */
  const selectedRecipients = useMemo(
    () => ({
      to: [value.to ?? []].flat().filter(Boolean) as string[],
      cc: [value.cc ?? []].flat().filter(Boolean) as string[],
      bcc: [value.bcc ?? []].flat().filter(Boolean) as string[],
    }),
    [value.to, value.cc, value.bcc]
  );

  // Falls back to the one shared object, so a consumer that passes no resolver
  // gets byte-identical props — and the same identities — as before.
  const toFieldProps = useMemo(
    () =>
      recipientSelectPropsFor?.('to', selectedRecipients) ??
      recipientSelectProps,
    [recipientSelectPropsFor, selectedRecipients, recipientSelectProps]
  );
  const ccFieldProps = useMemo(
    () =>
      recipientSelectPropsFor
        ? {
            ...asyncSelectProps(loadCcBcc),
            ...recipientSelectPropsFor('cc', selectedRecipients),
          }
        : ccBccSelectProps,
    [recipientSelectPropsFor, selectedRecipients, ccBccSelectProps, loadCcBcc]
  );
  const bccFieldProps = useMemo(
    () =>
      recipientSelectPropsFor
        ? {
            ...asyncSelectProps(loadCcBcc),
            ...recipientSelectPropsFor('bcc', selectedRecipients),
          }
        : ccBccSelectProps,
    [recipientSelectPropsFor, selectedRecipients, ccBccSelectProps, loadCcBcc]
  );

  const mergedTemplateSelectProps = useMemo(() => {
    if (!templateOptions) return templateSelectProps;

    const merged = {
      ...templateSelectProps,
      ...customTemplateSelectProps(templateEdited),
    };
    if (menuPortalTarget === undefined) return merged;

    // The same reason From needs it (above): kula's default is `document.body`,
    // which a kula Drawer covers with `pointer-events: none` — so the template
    // menu opened behind the panel and the click landed on the From row beneath
    // it, opening THAT menu instead. Every row that opens a menu has to portal
    // into the surface that asked.
    return {
      ...merged,
      reactSelectProps: {
        ...(templateSelectProps as { reactSelectProps?: object })
          ?.reactSelectProps,
        menuPortalTarget,
      },
    };
  }, [templateSelectProps, templateOptions, templateEdited, menuPortalTarget]);

  return (
    <Email
      apiRef={apiRef}
      showFrom
      showSubject
      showEditor
      enableCcBcc
      showTo={!!toOptions?.length}
      toMode={toMode}
      showTemplate={!!templateOptions}
      applyTemplateBodyToEditor={!!templateOptions}
      templateOptions={templateOptions}
      {...(mergedTemplateSelectProps
        ? { templateSelectProps: mergedTemplateSelectProps }
        : {})}
      value={value}
      onChange={handleChange}
      fromOptions={fromOptions}
      fromSelectProps={fromSelectProps}
      toOptions={toOptions}
      {...(toFieldProps ? { toSelectProps: toFieldProps } : {})}
      ccBccOptions={searchedCcBccOptions}
      ccSelectProps={ccFieldProps}
      bccSelectProps={bccFieldProps}
      submitAttempt={attempted}
      validateOnMount={attempted > 0}
      onInlineErrorChange={onInlineErrorChange}
      subjectVariableInputProps={subjectInputProps}
      disabled={disabled}
      placeholders={PLACEHOLDERS}
      footerAddon={footerAddon}
      editorProps={{
        ...(editorProps ?? {}),
        placeholder: editorPlaceholder,
        ...(editorClassName ? { className: editorClassName } : {}),
        // Where the editor's own popovers go — the attach-files list, the overflow
        // menu, the link and image panels. The same node the select menus portal
        // into, and for a related reason: a drawer is a focus-trapping modal, and a
        // body-portalled popover sits outside it. Radix's scroll lock then swallows
        // the wheel events over that popover, so a list longer than its panel could
        // not be scrolled at all.
        ...(menuPortalTarget === undefined
          ? {}
          : { portalContainer: menuPortalTarget }),
        // Pasted/inserted images POST to `/user_image_uploads` and the editor keeps the
        // returned URL; without it the body stores a base64 data URL.
        uploadInlineImage: uploadUserInlineImage,
        toolbarFeatures,
        variable: editorVariables,
        extraExtensions,
        toolbarStartSlot,
        toolbarEndSlot,
        onEditorReady,
        ...(attachments ?? {}),
      }}
    />
  );
};
